import React, { useEffect, useState, useCallback } from 'react';
import { Edit, Save, X, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../../config/settings-api';
import { spinWheelAPI } from '../../../config/spin-wheel-api';
import { helpers } from '../../../utils/helpers';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import './SpinWheelSettings.css';

const PRIZE_LABELS = {
  better_luck: 'Better luck',
  coins_50: '50 Coins',
  coins_30: '30 Coins',
  coins_20: '20 Coins',
  free_delivery: 'Free Delivery',
  free_delivery_40_off: '40 QAR OFF + Free Delivery',
  pct7_free_delivery: '7% OFF + Free Delivery',
  pct5_free_delivery: '5% OFF + Free Delivery',
  pct10_free_delivery: '10% OFF + Free Delivery',
  extra_spin: 'Extra Spin',
  free_product: 'Free Product',
  perfume_weekly_win: 'Able Weekly Win',
};

const DATE_FILTERS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

const GIFT_PHOTO_ACCEPT = 'image/png,image/webp,.png,.webp';

const isAllowedGiftPhotoFile = (file) => {
  if (!file) return false;
  const mime = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return mime === 'image/png' || mime === 'image/webp' || name.endsWith('.png') || name.endsWith('.webp');
};

const mapSpinWheelConfig = (config = {}) => ({
  weeklyWinnersCount: config.weeklyWinnersCount ?? 20,
  giftName: config.giftName || 'Perfume',
  giftPhoto: config.giftPhoto || '',
  isGiftEnabled: config.isGiftEnabled !== false,
  nextWinnerSelectionDate: config.nextWinnerSelectionDate
    ? new Date(config.nextWinnerSelectionDate).toISOString().slice(0, 10)
    : '',
});

const emptyStats = () => ({
  totalSpins: 0,
  tierCounts: { A: 0, B: 0, C: 0 },
  prizeCounts: {},
  coinsWonTotal: 0,
  coinsWonByTier: { A: 0, B: 0, C: 0 },
  coinWinCounts: { coins_50: 0, coins_30: 0, coins_20: 0 },
  prizeTierBreakdown: {},
});

const formatTierLine = (counts = {}) =>
  `A: ${counts.A || 0} · B: ${counts.B || 0} · C: ${counts.C || 0}`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const SpinWheelSettings = ({ adminData }) => {
  const canView = helpers.hasSpinWheelAccess(adminData);
  const canManage = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.MANAGE_SPIN_WHEEL);

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [config, setConfig] = useState(mapSpinWheelConfig());
  const [overviewStats, setOverviewStats] = useState(emptyStats());
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ ...mapSpinWheelConfig(), giftPhotoFile: null, removeGiftPhoto: false });
  const [dateFilter, setDateFilter] = useState('all_time');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [winnersHistory, setWinnersHistory] = useState([]);
  const [winnersPage, setWinnersPage] = useState(1);
  const [winnersPagination, setWinnersPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  const fetchStats = useCallback(async (filter, start = '', end = '') => {
    setStatsLoading(true);
    try {
      const response = await spinWheelAPI.getStats({
        dateFilter: filter,
        startDate: start,
        endDate: end,
      });
      if (response?.success) {
        setOverviewStats(response.data || emptyStats());
      }
    } catch (error) {
      toast.error('Failed to load spin wheel stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.getAllGeneralConfigs();
      if (response?.success) {
        setConfig(mapSpinWheelConfig(response.data?.spinWheelConfig));
      } else {
        toast.error(response?.msg || 'Failed to load spin wheel settings');
      }
    } catch (error) {
      toast.error('Failed to load spin wheel settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    fetchConfig();
  }, [canView]);

  useEffect(() => {
    if (!canView || activeTab !== 'overview') return;
    if (dateFilter === 'custom') {
      if (customStart && customEnd) {
        fetchStats('custom', customStart, customEnd);
      }
    } else {
      fetchStats(dateFilter);
    }
  }, [activeTab, dateFilter, customStart, customEnd, canView, fetchStats]);

  const fetchWinnersHistory = useCallback(async (page = 1) => {
    setWinnersLoading(true);
    try {
      const response = await spinWheelAPI.getWinnersHistory({ page, limit: 20 });
      if (response?.success) {
        setWinnersHistory(response.data?.items || []);
        setWinnersPagination(response.data?.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 1,
        });
        setWinnersPage(page);
      }
    } catch (error) {
      toast.error('Failed to load winners history');
    } finally {
      setWinnersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView || activeTab !== 'winners') return;
    fetchWinnersHistory(winnersPage);
  }, [activeTab, canView, winnersPage, fetchWinnersHistory]);

  const openEditModal = () => {
    setEditForm({
      ...config,
      giftPhotoFile: null,
      removeGiftPhoto: false,
    });
    setShowEditModal(true);
  };

  const handleGiftPhotoChange = (e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = '';

    if (!file) return;

    if (!isAllowedGiftPhotoFile(file)) {
      toast.error('Gift photo must be a PNG or WebP image');
      return;
    }

    setEditForm((prev) => ({
      ...prev,
      giftPhotoFile: file,
      removeGiftPhoto: false,
    }));
  };

  const handleSave = async () => {
    if (!canManage) {
      toast.error('You do not have permission to update spin wheel settings');
      return;
    }

    const count = Number(editForm.weeklyWinnersCount);
    if (!Number.isFinite(count) || count < 1 || count > 100) {
      toast.error('Winners count must be between 1 and 100');
      return;
    }

    if (editForm.giftPhotoFile && !isAllowedGiftPhotoFile(editForm.giftPhotoFile)) {
      toast.error('Gift photo must be a PNG or WebP image');
      return;
    }

    setSaving(true);
    try {
      const response = await settingsAPI.updateSpinWheelConfig({
        weeklyWinnersCount: count,
        giftName: editForm.giftName.trim(),
        nextWinnerSelectionDate: editForm.isGiftEnabled ? (editForm.nextWinnerSelectionDate || null) : null,
        giftPhotoFile: editForm.giftPhotoFile,
        isGiftEnabled: editForm.isGiftEnabled,
        removeGiftPhoto: editForm.removeGiftPhoto,
      });

      if (response?.success) {
        toast.success('Spin wheel settings saved');
        const mapped = mapSpinWheelConfig(response.data);
        setConfig(mapped);
        setShowEditModal(false);
      } else {
        toast.error(response?.msg || 'Failed to save spin wheel settings');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to save spin wheel settings');
    } finally {
      setSaving(false);
    }
  };

  const renderStatsBlock = (stats) => {
    const freeDeliveryTiers = stats.prizeTierBreakdown?.free_delivery || {};
    const pct5Tiers = stats.prizeTierBreakdown?.pct5_free_delivery || {};
    const prizeEntries = Object.entries(stats.prizeCounts || {})
      .sort((a, b) => b[1] - a[1]);

    return (
      <>
        <div className="sw-stats-grid">
          <div className="sw-stat-card">
            <div className="label">Total spins</div>
            <div className="value">{stats.totalSpins}</div>
            <div className="sw-stat-note">{formatTierLine(stats.tierCounts)}</div>
          </div>
          <div className="sw-stat-card">
            <div className="label">Coins won</div>
            <div className="value">{stats.coinsWonTotal}</div>
            <div className="sw-stat-note">{formatTierLine(stats.coinsWonByTier)}</div>
          </div>
          <div className="sw-stat-card">
            <div className="label">Free delivery</div>
            <div className="value">{stats.prizeCounts?.free_delivery || 0}</div>
            <div className="sw-stat-note">{formatTierLine(freeDeliveryTiers)}</div>
          </div>
          <div className="sw-stat-card">
            <div className="label">5% + Free delivery</div>
            <div className="value">{stats.prizeCounts?.pct5_free_delivery || 0}</div>
            <div className="sw-stat-note">{formatTierLine(pct5Tiers)}</div>
          </div>
        </div>

        {prizeEntries.length > 0 && (
          <div className="sw-rewards-won">
            <h3 className="sw-section-title">Rewards won</h3>
            <div className="sw-prize-grid">
              {prizeEntries.map(([key, count]) => (
                <div key={key} className="sw-prize-card">
                  <div className="name">{PRIZE_LABELS[key] || key}</div>
                  <div className="count">{count}</div>
                  <div className="sw-stat-note">
                    {formatTierLine(stats.prizeTierBreakdown?.[key])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="sw-tier-note">
          <strong>Note:</strong> Tier A = users with 0 delivered orders · Tier B = 1 delivered order · Tier C = 2+ delivered orders.
        </div>
      </>
    );
  };

  const renderWinnersHistory = () => {
    if (winnersLoading) {
      return <div className="sw-loading">Loading winners history...</div>;
    }

    if (!winnersHistory.length) {
      return (
        <p style={{ color: '#6b7280', margin: 0 }}>
          No weekly winner rounds recorded yet. Winners appear here after each selection is saved to the weekly winners collection.
        </p>
      );
    }

    return (
      <div className="sw-winners-list">
        {winnersHistory.map((round) => (
          <div key={round._id} className="sw-winner-round">
            <div className="sw-winner-round-header">
              <div>
                <strong>Selection date</strong>
                <span>{formatDate(round.selectionDate)}</span>
              </div>
              <div>
                <strong>Period</strong>
                <span>
                  {formatDate(round.periodStart)} → {formatDate(round.periodEnd)}
                </span>
              </div>
              <div>
                <strong>Winners</strong>
                <span>{round.winners?.length || 0}</span>
              </div>
            </div>
            <div className="sw-winner-table-wrap">
              <table className="sw-winner-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Phone</th>
                    <th>Gift</th>
                    <th>Spin ID</th>
                  </tr>
                </thead>
                <tbody>
                  {(round.winners || []).map((winner, index) => {
                    const user = winner.userId || {};
                    const name = winner.userName
                      || [user.firstName, user.lastName].filter(Boolean).join(' ')
                      || '—';
                    return (
                      <tr key={`${round._id}-${index}`}>
                        <td>
                          <div className="sw-winner-user">
                            {(winner.profilePicture || user.profilePicture) && (
                              <img
                                src={winner.profilePicture || user.profilePicture}
                                alt=""
                                className="sw-winner-avatar"
                              />
                            )}
                            <span>{name}</span>
                          </div>
                        </td>
                        <td>{user.phoneNumber || '—'}</td>
                        <td>
                          <div className="sw-winner-gift">
                            {winner.giftPhoto && (
                              <img src={winner.giftPhoto} alt="" className="sw-winner-gift-photo" />
                            )}
                            <span>{winner.giftName || '—'}</span>
                          </div>
                        </td>
                        <td className="sw-mono">{winner.spinId ? String(winner.spinId) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {winnersPagination.totalPages > 1 && (
          <div className="sw-pagination">
            <button
              type="button"
              className="sw-btn sw-btn-secondary"
              disabled={winnersPage <= 1}
              onClick={() => setWinnersPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {winnersPagination.page} of {winnersPagination.totalPages}
            </span>
            <button
              type="button"
              className="sw-btn sw-btn-secondary"
              disabled={winnersPage >= winnersPagination.totalPages}
              onClick={() => setWinnersPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!canView) {
    return (
      <div className="spin-wheel-page">
        <div className="sw-no-access">
          <h2>Access denied</h2>
          <p>You do not have permission to view spin wheel settings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="spin-wheel-page">
        <div className="sw-loading">Loading spin wheel...</div>
      </div>
    );
  }

  const photoPreview = editForm.giftPhotoFile
    ? URL.createObjectURL(editForm.giftPhotoFile)
    : (editForm.removeGiftPhoto ? '' : editForm.giftPhoto);

  return (
    <div className="spin-wheel-page">
      <div className="sw-header">
        <h1>Spin Wheel</h1>
        <p>Manage Able Weekly Win, view spin stats and reward analytics.</p>
      </div>

      <div className="sw-tabs">
        <button
          type="button"
          className={`sw-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          className={`sw-tab ${activeTab === 'winners' ? 'active' : ''}`}
          onClick={() => setActiveTab('winners')}
        >
          Winners history
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          <div className="sw-card">
            <div className="sw-card-header">
              <div>
                <h2 className="sw-card-title">Able Weekly Win</h2>
                <span className={`sw-badge ${config.isGiftEnabled ? 'on' : 'off'}`}>
                  {config.isGiftEnabled ? 'Gift enabled' : 'Gift hidden'}
                </span>
              </div>
              {canManage && (
                <button type="button" className="sw-btn sw-btn-primary" onClick={openEditModal}>
                  <Edit size={16} />
                  Edit
                </button>
              )}
            </div>

            {config.isGiftEnabled ? (
              <div className="sw-summary-grid">
                <div className="sw-summary-item">
                  <label>Winners count</label>
                  <span>{config.weeklyWinnersCount}</span>
                </div>
                <div className="sw-summary-item">
                  <label>Gift name</label>
                  <span>{config.giftName || '—'}</span>
                </div>
                <div className="sw-summary-item">
                  <label>Next selection date</label>
                  <span>{config.nextWinnerSelectionDate || '—'}</span>
                </div>
                <div className="sw-summary-item">
                  <label>Gift photo</label>
                  {config.giftPhoto ? (
                    <img src={config.giftPhoto} alt="Gift" className="sw-gift-photo" />
                  ) : (
                    <span>—</span>
                  )}
                </div>
              </div>
            ) : (
              <p style={{ color: '#6b7280', margin: 0 }}>
                Weekly win gift is turned off. Users will not see the gift button or reward details in the app.
              </p>
            )}
          </div>

          <div className="sw-card">
            <h2 className="sw-card-title">Spin stats</h2>
            <div className="sw-filters">
              <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                {DATE_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              {dateFilter === 'custom' && (
                <>
                  <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                  <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                </>
              )}
            </div>
            {statsLoading ? (
              <div className="sw-loading">Loading stats...</div>
            ) : (
              renderStatsBlock(overviewStats)
            )}
          </div>
        </>
      )}

      {activeTab === 'winners' && (
        <div className="sw-card">
          <h2 className="sw-card-title">Weekly winners history</h2>
          <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 16 }}>
            All weekly gift winner rounds with user, gift, and spin details.
          </p>
          {renderWinnersHistory()}
        </div>
      )}

      {showEditModal && (
        <div className="sw-modal-overlay" onClick={() => !saving && setShowEditModal(false)}>
          <div className="sw-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Able Weekly Win</h2>

            <div className="sw-toggle-row">
              <div>
                <strong>Show gift in app</strong>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                  When off, gift name, photo and selection date are hidden from the API.
                </p>
              </div>
              <input
                type="checkbox"
                checked={editForm.isGiftEnabled}
                onChange={(e) => setEditForm((prev) => ({ ...prev, isGiftEnabled: e.target.checked }))}
              />
            </div>

            <div className="sw-form-group">
              <label>Winners count</label>
              <input
                type="number"
                min="1"
                max="100"
                value={editForm.weeklyWinnersCount}
                onChange={(e) => setEditForm((prev) => ({ ...prev, weeklyWinnersCount: e.target.value }))}
              />
            </div>

            {editForm.isGiftEnabled && (
              <>
                <div className="sw-form-group">
                  <label>Gift name</label>
                  <input
                    type="text"
                    value={editForm.giftName}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, giftName: e.target.value }))}
                  />
                </div>

                <div className="sw-form-group">
                  <label>Next winner selection date</label>
                  <input
                    type="date"
                    value={editForm.nextWinnerSelectionDate}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, nextWinnerSelectionDate: e.target.value }))}
                  />
                </div>

                <div className="sw-form-group">
                  <label>Gift photo</label>
                  <input
                    type="file"
                    accept={GIFT_PHOTO_ACCEPT}
                    onChange={handleGiftPhotoChange}
                  />
                  <p className="sw-field-hint">PNG or WebP only</p>
                  {photoPreview && (
                    <img src={photoPreview} alt="Preview" className="sw-gift-photo" />
                  )}
                  {(editForm.giftPhoto || photoPreview) && (
                    <button
                      type="button"
                      className="sw-btn sw-btn-danger"
                      style={{ marginTop: 10 }}
                      onClick={() => setEditForm((prev) => ({
                        ...prev,
                        giftPhoto: '',
                        giftPhotoFile: null,
                        removeGiftPhoto: true,
                      }))}
                    >
                      <Trash2 size={14} />
                      Remove photo
                    </button>
                  )}
                </div>
              </>
            )}

            <div className="sw-modal-actions">
              <button type="button" className="sw-btn sw-btn-secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
                <X size={16} />
                Cancel
              </button>
              <button type="button" className="sw-btn sw-btn-primary" onClick={handleSave} disabled={saving}>
                <Save size={16} />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinWheelSettings;
